// â”€â”€â”€ Promote Contact â†’ User modal (Phase 7) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Compact modal: contact already has name + email + dept. Admin only picks
// roles + isLeader, then triggers inviteUser Cloud Function via service.

import React, { useState } from 'react';
import type { Contact } from '../types';
import { promoteContactToUser } from '../services/contactsService';
import { isSyntheticEmail } from '../lib/email';

const OKR_ROLES  = ['viewer', 'editor', 'admin', 'superadmin'] as const;
const EPR_ROLES  = ['employee', 'leader', 'hr_admin', 'admin', 'superadmin'] as const;
const PORT_ROLES = ['viewer', 'admin', 'superadmin'] as const;

interface Props {
  open: boolean;
  contact: Contact | null;
  onClose: () => void;
  onPromoted: (uid: string) => void;
}

const PromoteContactModal: React.FC<Props> = ({ open, contact, onClose, onPromoted }) => {
  const [okrRole, setOkrRole]       = useState<string>('viewer');
  const [eprRole, setEprRole]       = useState<string>('employee');
  const [portalRole, setPortalRole] = useState<string>('viewer');
  const [isLeader, setIsLeader]     = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);

  if (!open || !contact) return null;

  const cannotPromote = isSyntheticEmail(contact.email);

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!contact) return;
    setSubmitting(true);
    setError(null);
    try {
      const { uid } = await promoteContactToUser(contact.id, {
        appRoles: {
          okr: okrRole as any,
          epr: eprRole as any,
          portal: portalRole as any,
        },
        isLeader,
      });
      onPromoted(uid);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto my-8"
             style={{ fontFamily: 'Inter, sans-serif' }}>
          <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-stone-900">Grant App Login</h2>
              <p className="text-xs text-stone-500 mt-0.5">
                Creates Firebase Auth account + <code>users/{'{uid}'}</code> doc linked to this contact.
              </p>
            </div>
            <button onClick={onClose} className="text-stone-400 hover:text-stone-700">
              <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Contact summary */}
            <div className="p-3 bg-stone-50 border border-stone-200 rounded text-sm">
              <p className="font-semibold text-stone-800">{contact.displayName}</p>
              <p className="text-xs text-stone-500">{contact.email}</p>
              {contact.department && (
                <p className="text-xs text-stone-500">Dept: {contact.department}</p>
              )}
            </div>

            {cannotPromote && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                âš  Synthetic email cannot sign in. Set a real email on the contact first.
              </div>
            )}

            {/* Roles */}
            <div className="grid grid-cols-1 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-stone-700">OKR Tracker role</span>
                <select
                  value={okrRole}
                  onChange={(e) => setOkrRole(e.target.value)}
                  className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
                  disabled={submitting || cannotPromote}
                >
                  {OKR_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-stone-700">EPR role</span>
                <select
                  value={eprRole}
                  onChange={(e) => setEprRole(e.target.value)}
                  className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
                  disabled={submitting || cannotPromote}
                >
                  {EPR_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-stone-700">Portal role</span>
                <select
                  value={portalRole}
                  onChange={(e) => setPortalRole(e.target.value)}
                  className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
                  disabled={submitting || cannotPromote}
                >
                  {PORT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs text-stone-700">
                <input
                  type="checkbox"
                  checked={isLeader}
                  onChange={(e) => setIsLeader(e.target.checked)}
                  disabled={submitting || cannotPromote}
                />
                Is a leader (gates OKR dept-lead features + EPR HR framework)
              </label>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-stone-100">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 rounded-lg border border-stone-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || cannotPromote}
                className="px-5 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
                style={{ background: '#011132' }}
              >
                {submitting ? 'Grantingâ€¦' : 'Grant Login'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default PromoteContactModal;
